import { useMemo, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Paperclip, X } from "lucide-react";
import { Button } from "../../primitives/button";
import { useAuth } from "../../../auth/hooks";
import type * as API from "@shared-core/data/api-types";
import {
  TicketCommentTimeoutError,
  type PostTicketCommentResult,
} from "@shared-core/domain/post-ticket-comment";

type CommentEntry = {
  id: string;
  created_at: string;
  user_id: string | null | undefined;
  text: string;
  attachments: API.WorkflowAttachment[];
};

// Kept in step with the backend allowlist (workflow_module/bootstrap.py) —
// a convenience filter on the file picker, never the enforcement point.
// Screenshots are the expected case, but the same upload path already
// accepts PDFs/office docs, so there's no reason to narrow it here.
const ACCEPTED_FILE_TYPES =
  ".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx";

interface TicketCommentsProps {
  /** Comment-type TicketActivity entries only (filtered by the caller). */
  comments: API.TicketActivity[];
  /** Legacy per-ticket notes (wf_note) — the same underlying idea as a
   * comment, from before the two systems were unified. Shown identically,
   * read-only; new entries always go through the composer below. */
  notes?: (API.Note | API.NoteInTicketView)[];
  resolveUserName: (userId: string) => string;
  /** The caller posts the comment then uploads each file against it (see
   * postTicketComment). Rejects when the comment was not posted; the draft
   * is kept until this resolves. */
  onAddComment: (
    body: string,
    files: File[],
  ) => Promise<Pick<PostTicketCommentResult, "failedFiles">>;
  onDownloadAttachment: (attachment: API.WorkflowAttachment) => void;
}

function describeFailure(err: unknown): string {
  if (err instanceof TicketCommentTimeoutError) {
    return err.message;
  }
  return "Your comment couldn't be posted. It's still here, so press Post to try again.";
}

export function TicketComments({
  comments,
  notes = [],
  resolveUserName,
  onAddComment,
  onDownloadAttachment,
}: TicketCommentsProps) {
  const { user } = useAuth();
  const [commentText, setCommentText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const entries = useMemo<CommentEntry[]>(() => {
    const fromActivity = comments.map((a): CommentEntry => ({
      id: a.id,
      created_at: a.created_at,
      user_id: a.user_id,
      text: a.body ?? "",
      attachments: a.attachments ?? [],
    }));
    const fromNotes = notes.map((n): CommentEntry => ({
      id: n.id,
      created_at: n.created_at,
      user_id: n.user_id,
      text: n.note_title ? `${n.note_title}\n${n.note_text}` : n.note_text,
      attachments: [],
    }));
    return [...fromActivity, ...fromNotes].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
  }, [comments, notes]);

  // Picking more files adds to the selection rather than replacing it.
  const handleFilesPicked = (picked: FileList | null) => {
    const incoming = Array.from(picked ?? []);
    setSelectedFiles((prev) => [
      ...prev,
      ...incoming.filter(
        (file) =>
          !prev.some((p) => p.name === file.name && p.size === file.size),
      ),
    ]);
    // Cleared so picking the same file again (after removing it) still fires.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    const body = commentText.trim();
    if (!body && selectedFiles.length === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const { failedFiles } = await onAddComment(body, selectedFiles);
      setCommentText("");
      setSelectedFiles(failedFiles);
      if (failedFiles.length > 0) {
        setSubmitError(
          `Your comment was posted, but ${failedFiles
            .map((f) => f.name)
            .join(", ")} didn't attach. Press Post to try attaching again.`,
        );
      }
    } catch (err) {
      setSubmitError(describeFailure(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const actorName = (userId: string | null | undefined) => {
    if (!userId) return "System";
    return userId === user?.id ? "You" : resolveUserName(userId);
  };

  return (
    <div>
      <h3 className="text-sm font-medium mb-2">
        Comments{entries.length > 0 ? ` (${entries.length})` : ""}
      </h3>
      <div className="space-y-3 mb-3">
        {entries.map((entry) => (
          <div key={entry.id} className="text-sm">
            <div className="flex items-baseline gap-2">
              <span className="font-medium">{actorName(entry.user_id)}</span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(entry.created_at), {
                  addSuffix: true,
                })}
              </span>
            </div>
            {entry.text && (
              <p className="mt-0.5 whitespace-pre-wrap">{entry.text}</p>
            )}
            {entry.attachments.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {entry.attachments.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => onDownloadAttachment(attachment)}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground hover:bg-muted"
                  >
                    <Paperclip className="size-3 text-muted-foreground" />
                    {attachment.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedFiles.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedFiles.map((file) => (
            <div
              key={`${file.name}-${file.size}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs"
            >
              <Paperclip className="size-3 text-muted-foreground" />
              {file.name}
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={isSubmitting}
                onClick={() =>
                  setSelectedFiles((prev) => prev.filter((f) => f !== file))
                }
              >
                <X className="size-3 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}

      {submitError && (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {submitError}
        </p>
      )}

      <div className="flex items-start gap-2">
        <textarea
          value={commentText}
          readOnly={isSubmitting}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Write a comment..."
          rows={2}
          className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          className="sr-only"
          aria-label="Attach files to this comment"
          onChange={(e) => handleFilesPicked(e.target.files)}
        />
        <Button
          size="sm"
          variant="outline"
          type="button"
          aria-label="Attach files"
          disabled={isSubmitting}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="size-3.5" />
        </Button>
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={
            (!commentText.trim() && selectedFiles.length === 0) || isSubmitting
          }
        >
          {isSubmitting ? "Posting..." : "Post"}
        </Button>
      </div>
    </div>
  );
}
